import type {
  QuickBooksFpaForecastGrain,
  QuickBooksFpaMlForecastArtifactsEnsureResponse,
  QuickBooksFpaMlForecastPackageSnapshotResponse,
  QuickBooksFpaMlStatementForecastResponse,
} from '../../../data/ServerApiClient'
import { formatAsOf, trimStr } from './cfoFormat'
import type { CfoShellCacheState } from './CfoPageShell'

export const monthOffsetIso = (period: string | null | undefined, offset: number): string | null => {
  const match = /^(\d{4})-(\d{2})/.exec(String(period || ''))
  if (!match) return null
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1 + offset, 1))
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-01`
}

export const forecastStudioModelHistoryStartPeriod = (anchorPeriod: string | null | undefined): string | null => {
  const match = /^(\d{4})-\d{2}/.exec(String(anchorPeriod || ''))
  if (!match) return null
  return `${Number(match[1]) - 2}-01-01`
}

const isoDay = (date: Date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`

const parseIsoDay = (value: string | null | undefined): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''))
  if (!match) return null
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
}

export const addDaysIso = (value: string, days: number): string => {
  const date = parseIsoDay(value)
  if (!date) return value
  date.setUTCDate(date.getUTCDate() + days)
  return isoDay(date)
}

export const monthStartIso = (value: string): string => {
  const date = parseIsoDay(value)
  if (!date) return value
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-01`
}

export const weekStartIso = (value: string): string => {
  const date = parseIsoDay(value)
  if (!date) return value
  const day = date.getUTCDay()
  const mondayOffset = day === 0 ? -6 : 1 - day
  date.setUTCDate(date.getUTCDate() + mondayOffset)
  return isoDay(date)
}

export const forecastStudioVisibleHistoryStartPeriod = (
  anchorPeriod: string | null | undefined,
  historyPeriods: number,
  grain: QuickBooksFpaForecastGrain | string = 'month',
): string | null => {
  const count = Math.max(0, Math.floor(historyPeriods) || 0)
  if (count <= 0) return null
  const anchorDay = String(anchorPeriod || '').slice(0, 10)
  if (!parseIsoDay(anchorDay)) return null
  if (grain === 'daily') return addDaysIso(anchorDay, 1 - count)
  if (grain === 'weekly') return addDaysIso(weekStartIso(anchorDay), -7 * (count - 1))
  const anchorMonth = monthStartIso(anchorDay)
  return monthOffsetIso(anchorMonth, 1 - count)
}

export const forecastStartIso = (snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse, selectedEndPeriod: string | null | undefined): string | null => {
  const requested = selectedEndPeriod ? monthOffsetIso(selectedEndPeriod, 1) : null
  const candidates = [requested, snapshot.start_date || null, snapshot.package?.start_period || null].filter(Boolean) as string[]
  if (!candidates.length) return null
  return candidates.sort()[candidates.length - 1]
}

export const cashForecastHorizonEndIso = (start: string, todayIso = isoDay(new Date())): string => {
  const today = parseIsoDay(todayIso) ? todayIso : start
  const currentPlusThirteenWeeks = addDaysIso(today, 91)
  const startPlusThirteenWeeks = addDaysIso(start, 91)
  return [currentPlusThirteenWeeks, startPlusThirteenWeeks].sort()[1]
}

export const daysBetweenIso = (start: string, end: string): number => {
  const startDate = parseIsoDay(start)
  const endDate = parseIsoDay(end)
  if (!startDate || !endDate) return 0
  return Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 86_400_000))
}

export const daysInMonthIso = (monthStart: string): number => {
  const nextMonth = monthOffsetIso(monthStartIso(monthStart), 1)
  return nextMonth ? Math.max(1, daysBetweenIso(monthStartIso(monthStart), nextMonth)) : 30
}

export const periodEndExclusiveIso = (period: string, grain: QuickBooksFpaForecastGrain | string): string =>
  grain === 'daily' ? addDaysIso(period, 1) : grain === 'weekly' ? addDaysIso(period, 7) : monthOffsetIso(period, 1) || addDaysIso(period, 31)

export const actualPeriodsForForecastGrain = (
  start: string | null | undefined,
  end: string | null | undefined,
  grain: QuickBooksFpaForecastGrain | string,
): string[] => {
  const startDay = String(start || '').slice(0, 10)
  const endDay = String(end || '').slice(0, 10)
  if (!parseIsoDay(startDay) || !parseIsoDay(endDay) || startDay > endDay) return []
  if (grain === 'daily') {
    const count = daysBetweenIso(startDay, addDaysIso(endDay, 1))
    return Array.from({ length: count }, (_value, index) => addDaysIso(startDay, index))
  }
  if (grain === 'weekly') {
    const firstWeek = weekStartIso(startDay)
    const lastWeek = weekStartIso(endDay)
    const count = Math.max(1, Math.floor(daysBetweenIso(firstWeek, lastWeek) / 7) + 1)
    return Array.from({ length: count }, (_value, index) => addDaysIso(firstWeek, index * 7))
  }
  const firstMonth = monthStartIso(startDay)
  const lastMonth = monthStartIso(endDay)
  const periods: string[] = []
  let cursor = firstMonth
  while (cursor <= lastMonth && periods.length < 240) {
    periods.push(cursor)
    const next = monthOffsetIso(cursor, 1)
    if (!next || next === cursor) break
    cursor = next
  }
  return periods
}

export const dateRangeOverlapDays = (aStart: string, aEnd: string, bStart: string, bEnd: string): number => {
  const start = [aStart, bStart].sort()[1]
  const end = [aEnd, bEnd].sort()[0]
  return daysBetweenIso(start, end)
}

export const FORECAST_STUDIO_DEFAULT_FORECAST_MONTHS = 24
export const FORECAST_STUDIO_MAX_FORECAST_MONTHS = 24
export const FORECAST_STUDIO_SNAPSHOT_TIMEOUT_MS = 15_000
export const FORECAST_STUDIO_CASH_ARTIFACT_TIMEOUT_MS = 60_000
export const FORECAST_STUDIO_ENSURE_TIMEOUT_MS = 20_000
export const FORECAST_STUDIO_ENSURE_POLL_MS = 3_500
export const FORECAST_STUDIO_ENSURE_MAX_POLLS = 120

export type ForecastBuildStage = 'syncing' | 'analyzing_history' | 'building_forecast' | 'publishing'

export const FORECAST_BUILD_STAGES: Array<{ stage: ForecastBuildStage; label: string }> = [
  { stage: 'syncing', label: 'Syncing QuickBooks data' },
  { stage: 'analyzing_history', label: 'Analyzing history' },
  { stage: 'building_forecast', label: 'Building forecast' },
  { stage: 'publishing', label: 'Publishing package' },
]

export type ForecastStudioPackageBuildState = {
  status: string
  message: string
  asOfDate: string
  jobId?: string | null
  validation?: any
  stage?: ForecastBuildStage | null
  progressPct?: number | null
  etaSeconds?: number | null
  userMessage?: string | null
  nextSteps?: string[]
}

// Optional staged-progress fields the ensure endpoint MAY return. The backend
// does not send them yet; every field degrades independently to the generic
// build copy when absent.
export type ForecastStudioEnsureProgressFields = {
  stage?: string | null
  progress_pct?: number | string | null
  eta_seconds?: number | string | null
  user_message?: string | null
  next_steps?: unknown
}

const forecastBuildStageFrom = (raw: string | null | undefined): ForecastBuildStage | null => {
  const stage = trimStr(raw).toLowerCase()
  return FORECAST_BUILD_STAGES.some((entry) => entry.stage === stage) ? (stage as ForecastBuildStage) : null
}

const boundedNumberOrNull = (raw: number | string | null | undefined, min: number, max: number): number | null => {
  if (raw == null || raw === '') return null
  const value = Number(raw)
  if (!Number.isFinite(value)) return null
  return Math.min(max, Math.max(min, value))
}

export const forecastSnapshotMatchesAnchor = (
  snapshot: Pick<QuickBooksFpaMlForecastPackageSnapshotResponse, 'package'> | null | undefined,
  anchorIso: string | null | undefined,
): boolean => {
  const anchor = trimStr(anchorIso)
  if (!anchor) return true
  const sourceWindowEnd = trimStr(snapshot?.package?.source_window_end)
  if (!sourceWindowEnd) return false
  return sourceWindowEnd.slice(0, 7) === anchor.slice(0, 7)
}

export const forecastStudioPackageBuildState = (
  asOfDate: string | null | undefined,
  ensured: (Pick<QuickBooksFpaMlForecastArtifactsEnsureResponse, 'status' | 'message' | 'job_id' | 'validation'> & ForecastStudioEnsureProgressFields) | null | undefined,
): ForecastStudioPackageBuildState | null => {
  const status = trimStr(ensured?.status)
  if (!status || status === 'ready') return null
  const monthLabel = trimStr(asOfDate) ? formatAsOf(trimStr(asOfDate)) : 'the selected month'
  const fallbackMessage = status === 'queued' || status === 'running'
    ? `Building the ${monthLabel} forecast package…`
    : status === 'missing_source_data'
      ? `Synced QuickBooks data does not cover ${monthLabel} yet.`
      : `The ${monthLabel} forecast package build failed.`
  const nextSteps = Array.isArray(ensured?.next_steps)
    ? (ensured?.next_steps as unknown[]).map((step) => trimStr(step as any)).filter(Boolean)
    : []
  return {
    status,
    message: trimStr(ensured?.message) || fallbackMessage,
    asOfDate: trimStr(asOfDate),
    jobId: ensured?.job_id || null,
    validation: ensured?.validation ?? null,
    stage: forecastBuildStageFrom(ensured?.stage),
    progressPct: boundedNumberOrNull(ensured?.progress_pct, 0, 100),
    etaSeconds: boundedNumberOrNull(ensured?.eta_seconds, 0, Number.MAX_SAFE_INTEGER),
    userMessage: trimStr(ensured?.user_message) || null,
    nextSteps,
  }
}

export type ForecastBuildFailureActionKind = 'sync' | 'pick_prior_month' | 'retry'

export type ForecastBuildFailureGuidance = {
  message: string
  actions: Array<{ label: string; kind: ForecastBuildFailureActionKind }>
}

// Known terminal failure shapes from the ensure endpoint mapped to plain
// guidance plus recovery actions the page can wire to real triggers.
export const forecastBuildFailureGuidance = (
  status: string | null | undefined,
  validationJson: any,
  asOfDate?: string | null,
): ForecastBuildFailureGuidance | null => {
  const normalized = trimStr(status)
  if (!normalized || normalized === 'ready' || normalized === 'queued' || normalized === 'running') return null
  const monthLabel = trimStr(asOfDate) ? formatAsOf(trimStr(asOfDate)) : 'the selected month'
  if (normalized === 'missing_source_data') {
    return {
      message: `QuickBooks data for ${monthLabel} hasn't synced yet.`,
      actions: [
        { label: 'Sync & check again', kind: 'sync' },
        { label: 'Use latest closed month', kind: 'pick_prior_month' },
      ],
    }
  }
  const artifactError = trimStr(
    validationJson?.artifact_validation?.error
    || validationJson?.last_error
    || '',
  )
  if (artifactError) {
    return {
      message: `The ${monthLabel} forecast artifacts are being refreshed. ${artifactError}`,
      actions: [
        { label: 'Refresh forecast', kind: 'retry' },
        { label: 'Use latest closed month', kind: 'pick_prior_month' },
      ],
    }
  }
  return {
    message: `The ${monthLabel} forecast package build failed.`,
    actions: [
      { label: 'Retry build', kind: 'retry' },
      { label: 'Use latest closed month', kind: 'pick_prior_month' },
    ],
  }
}

// Warns when sub-monthly cash rows are an even allocation of monthly line
// metadata (no transaction-timed daily artifacts) so daily/weekly figures are
// never mistaken for payment-timed cash.
export const degradedCashNotice = (
  validationJson: any,
  grain: QuickBooksFpaForecastGrain | string,
  usesLegacyRows: boolean,
): string | null => {
  if (grain !== 'daily' && grain !== 'weekly') return null
  const rollupBasis = trimStr(validationJson?.rollup_basis)
  const allocatedFromMonthly = usesLegacyRows
    || validationJson?.monthly_line_backed === true
    || rollupBasis === 'legacy_monthly_line_metadata'
    || trimStr(validationJson?.monthly_rollup_basis) === 'line_metadata'
  if (!allocatedFromMonthly) return null
  const grainLabel = grain === 'daily' ? 'Daily' : 'Weekly'
  return `${grainLabel} figures are an even spread of monthly totals — not transaction-timed.`
}

// The default anchor for first load: the most recent fully-closed month the
// period options offer, optionally capped by the synced source window.
export const defaultForecastAnchor = (
  periodOptions: Array<{ id: string }> | null | undefined,
  sourceWindowEnd: string | null | undefined,
  todayIso: string,
): string | null => {
  const today = parseIsoDay(String(todayIso || '').slice(0, 10))
  if (!today) return null
  const currentMonth = monthStartIso(String(todayIso).slice(0, 10))
  const coverageEnd = trimStr(sourceWindowEnd).slice(0, 10)
  const coverageMonth = coverageEnd && parseIsoDay(coverageEnd) ? monthStartIso(coverageEnd) : ''
  let best: string | null = null
  for (const option of periodOptions || []) {
    const raw = trimStr(option?.id).slice(0, 10)
    if (!parseIsoDay(raw)) continue
    const month = monthStartIso(raw)
    if (month >= currentMonth) continue
    if (coverageMonth && month > coverageMonth) continue
    if (!best || month > best) best = month
  }
  return best
}

export const forecastStudioPackageBuildNotice = (
  build: ForecastStudioPackageBuildState | null | undefined,
): { tone: 'building' | 'failed'; title: string; message: string; detail: string } | null => {
  if (!build) return null
  const building = build.status === 'queued' || build.status === 'running'
  const detail = trimStr(
    build.validation?.artifact_validation?.error
    || build.validation?.last_error
    || '',
  )
  return {
    tone: building ? 'building' : 'failed',
    title: building
      ? build.status === 'running' ? 'Forecast package building' : 'Forecast package queued'
      : build.status === 'missing_source_data' ? 'Forecast source data missing' : 'Forecast package build failed',
    message: build.message,
    detail,
  }
}

export const shouldLoadForecastAnalyticsSidecars = (
  bootstrapLoading: boolean,
  hasBootstrap: boolean,
  cfoAnalyticsReady: boolean | null | undefined,
): boolean => !bootstrapLoading && hasBootstrap && cfoAnalyticsReady !== false

export const forecastCacheStateForPage = (
  cacheState: CfoShellCacheState | null | undefined,
  forecastUsesMaterializedArtifacts: boolean,
  forecastPackageId: string | null | undefined,
): CfoShellCacheState | null => {
  if (cacheState?.tone === 'failed' && forecastUsesMaterializedArtifacts && trimStr(forecastPackageId)) {
    return {
      tone: 'ready',
      label: 'Forecast cached',
      title: `Forecast package artifacts are loaded locally. Shared CFO analytics status: ${cacheState.title || cacheState.label}`,
    }
  }
  return cacheState || null
}

export const forecastStudioVisibleActualHistoryError = (args: {
  actualStatementError?: string | null
  actualBasisError?: string | null
  actualDetailCashError?: string | null
  statementKind?: string | null
  directCashForecast?: boolean
}): string => {
  if (args.directCashForecast && args.statementKind === 'cash_flow') return ''
  return trimStr(args.actualStatementError || args.actualBasisError || args.actualDetailCashError || '')
}

export const forecastStudioRenderedPackageLabel = (
  forecast: Pick<QuickBooksFpaMlStatementForecastResponse, 'statement' | 'package'> | null | undefined,
): string => {
  const periods = forecast?.statement?.periods || []
  const first = periods[0] || forecast?.package?.start_period || null
  const last = periods[periods.length - 1] || forecast?.package?.end_period || first
  return first ? `${formatAsOf(first)} to ${formatAsOf(last || first)}` : 'No active package'
}

export const forecastStudioSnapshotMode = (grain: QuickBooksFpaForecastGrain | string): 'monthly' | 'cash_artifacts' =>
  grain === 'month' ? 'monthly' : 'cash_artifacts'
